import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LogIn } from "lucide-react";
import { useNavigate } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";

interface AuthGatedDialogProps {
  trigger: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

export const AuthGatedDialog = ({ trigger, title, children }: AuthGatedDialogProps) => {
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const navigate = useNavigate();

  const handleClick = async () => {
    if (isCheckingAuth) return;

    setIsCheckingAuth(true);
    try {
      const { data, error } = await supabase.auth.getUser();
      const isAuthenticated = !error && !!data.user;

      if (isAuthenticated) {
        setShowLoginPrompt(false);
        setShowContent(true);
      } else {
        setShowContent(false);
        setShowLoginPrompt(true);
      }
    } catch {
      setShowContent(false);
      setShowLoginPrompt(true);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const renderedTrigger = React.isValidElement(trigger)
    ? React.cloneElement(
        trigger as React.ReactElement<{ onClick?: (event: React.MouseEvent) => void; disabled?: boolean }>,
        {
          onClick: (event: React.MouseEvent) => {
            event.preventDefault();
            void handleClick();
          },
          disabled: isCheckingAuth,
        }
      )
    : (
        <Button onClick={() => void handleClick()} disabled={isCheckingAuth} className="w-full">
          {trigger}
        </Button>
      );

  return (
    <>
      {renderedTrigger}

      <Dialog open={showLoginPrompt} onOpenChange={setShowLoginPrompt}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Login Required</DialogTitle>
            <DialogDescription>
              Please log in or create an account to submit an inquiry or schedule a viewing.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 pt-2">
            <Button onClick={() => navigate("/auth")} className="w-full">
              <LogIn className="h-4 w-4 mr-2" />
              Login / Register
            </Button>
            <Button variant="outline" onClick={() => setShowLoginPrompt(false)} className="w-full">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showContent} onOpenChange={setShowContent}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          {children}
        </DialogContent>
      </Dialog>
    </>
  );
};
